import { Component, EventEmitter, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CurrentBenchmark } from 'src/app/models/current-benchmark.model';
import { MatTabChangeEvent } from "@angular/material/tabs";
import { CurrentPortfolio } from "src/app/models/current-portfolio.model";
import { StrategiesService } from 'src/app/services/strategies.service';
import { FactorStrategy } from 'src/app/models/factor-strategy.model';
import { ClustersService } from 'src/app/services/clusters.service';
import { StrategyParameters } from 'src/app/models/strategy-parameters.model';
import { Cluster } from 'src/app/models/cluster.model';
import { NotificationsService } from 'src/app/services/notifications.service';
import { ListsService } from 'src/app/services/lists.service';
import { MatHorizontalStepper } from '@angular/material/stepper';

@Component({
  selector: 'app-strategy-customize',
  templateUrl: './strategy-customize.component.html',
  styleUrls: ['./strategy-customize.component.scss']
})
export class StrategyCustomizeComponent implements OnInit, OnDestroy {

  @Output() changeBlock = new EventEmitter<string>();
  @ViewChild(MatHorizontalStepper) stepper: MatHorizontalStepper;

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  strategyBenchmark: CurrentBenchmark;
  strategyBenchmarkSubscription: Subscription;

  currentOriginal: CurrentPortfolio;
  currentOriginalSubscription: Subscription;
  defaultStrategy: FactorStrategy;
  defaultStrategySubscription: Subscription;
  customizedStrategy: FactorStrategy;
  customizedStrategySubscription: Subscription;

  hasOriginal: boolean = false;
  hasDefault: boolean = false;
  hasCustomized: boolean = false;
  isComputing: boolean = false;
  disableActions: boolean = false;

  strategyParameters: StrategyParameters;
  strategyParametersSubscription: Subscription;

  sliderRiskTolerance: number = 25;
  sliderMacroOutlook: number = 25;
  sliderMacroStability: number = 25;
  sliderRiskOn: number = 25;
  sliderStateVariable: number = 0;
  sliderRiskToleranceW: number = 0;
  sliderMacroOutlookW: number = 0;
  sliderMacroStabilityW: number = 0;
  sliderRiskOnW: number = 0;
  sliderStateVariableW: number = 0;

  totalPriorities: number = 100;
  totalWeights: number = 100;

  selectedPage: string = 'exposure';
  selectedPage2: string = 'rolling';
  selectedAnalyticsTab: string = 'absolute';
  selectedAnalyticsTab2: string = 'weights';

  macroVariablesList;
  stateVariablesList;

  selectBlock: string = '';

  showResults: boolean = true;

  panelOpenState = false;

  spotBreakdownData: any;

  stepPosition: number = 0;

  strategyFirstStepPage: any = {
    MinVar: 'volatility',
    EFRP:   'risk_contributions',
    EFW:    'exposure',
  };

  prevStepPage: any = {
    volatility: {
      MinVar: 'volatility',
      EFRP:   'risk_contributions',
      EFW:    'exposure',
    }
  }

  nextStepPage: any = {
    exposure:           'volatility',
    risk_contributions: 'volatility',
    volatility:         undefined,
  }

  constructor(private clustersService: ClustersService,
              private listsService: ListsService,
              private strategiesService: StrategiesService,
              private notificationsService: NotificationsService) {
    this.macroVariablesList = this.listsService.getMacroVariablesList();
    this.stateVariablesList = this.listsService.getStateVariablesList();
  }

  ngOnInit(): void {
    this.subscribeToCurrentCluster();
    this.subscribeToCurrentOriginal();

    this.subscribeToCurrentDefaultStrategy();
    this.subscribeToStrategyParameters();
  }

  ngOnDestroy() {
    if(this.currentClusterSubscription!=undefined) { this.currentClusterSubscription.unsubscribe(); }
    if(this.currentOriginalSubscription!=undefined) { this.currentOriginalSubscription.unsubscribe(); }
    if(this.strategyBenchmarkSubscription!=undefined) { this.strategyBenchmarkSubscription.unsubscribe(); }
    if(this.strategyParametersSubscription!=undefined) { this.strategyParametersSubscription.unsubscribe(); }

    if(this.defaultStrategySubscription!=undefined) { this.defaultStrategySubscription.unsubscribe(); }
    if(this.customizedStrategySubscription!=undefined) { this.customizedStrategySubscription.unsubscribe(); }
  }

  subscribeToCurrentCluster() {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (cluster: Cluster) => {
        this.currentCluster = cluster;
      }
    );
    this.clustersService.emitCurrentCluster();
  }

  subscribeToCurrentOriginal() {
    this.currentOriginalSubscription = this.clustersService.currentOriginalSubject.subscribe(
      (original_portfolio: CurrentPortfolio) => {
        this.currentOriginal = original_portfolio;
        this.hasOriginal = (original_portfolio!=undefined);
      }
    );
    this.clustersService.emitCurrentOriginal();
  }

  subscribeToCurrentDefaultStrategy() {
    this.defaultStrategySubscription = this.strategiesService.defaultStrategySubject.subscribe(
      (strategy: FactorStrategy) => {
        this.defaultStrategy = strategy;
        this.hasDefault = (strategy!=undefined);
        if (!this.hasCustomized && this.hasDefault) {
          this.customizedStrategy = this.defaultStrategy;
        }
        this.onShowHideResults(true);
      }
    );
    this.customizedStrategySubscription = this.strategiesService.customizedStrategySubject.subscribe(
      (strategy: FactorStrategy) => {
        this.customizedStrategy = strategy;
        this.hasCustomized = (strategy!=undefined);
        if (!this.hasCustomized && this.hasDefault) {
          this.customizedStrategy = this.defaultStrategy;
        }
        this.onShowHideResults(true);
        this.onDateChange();
      }
    );
    this.strategyBenchmarkSubscription = this.strategiesService.strategyBenchmarkSubject.subscribe(
      (benchmark: CurrentBenchmark) => {
        this.strategyBenchmark = benchmark;
      }
    );

    this.strategiesService.emitDefaultStrategy();
    this.strategiesService.emitCustomizedStrategy();
    this.strategiesService.emitStrategyBenchmark();
  }

  subscribeToStrategyParameters() {
    this.strategyParametersSubscription = this.strategiesService.strategyParametersSubject.subscribe(
      (strategyParameters: StrategyParameters) => {
        this.strategyParameters = strategyParameters;
        if (strategyParameters) {
          this.selectedPage = this.strategyFirstStepPage[this.strategyParameters.strategy_type.alias];
        }
      }
    );
    this.strategiesService.emitStrategyParameters();
  }

  formatLabel(value: number) {
    return Math.round(1000*value)/10;
  }

  onParameterChange() {
    this.strategyParameters.computeFactorIntensityContribution();
    this.strategyParameters.computeFactorRiskContributionPct();
  }

  async onCompute(custom_step: string) {
    this.notificationsService.runSpinner();
    this.isComputing = true;
    this.disableActions = true;

    try {
      this.strategyParameters.customization(custom_step, true);
      await this.strategiesService.computeCustomStrategy(this.strategyParameters, this.currentCluster);
      this.notificationsService.notify('computation-success');
    } catch (e) {
      // Display error
      this.strategyParameters.customization(custom_step, false);
      this.notificationsService.notify('computation-failed');
    }

    this.onShowHideResults(this.strategyParameters.is_customized);

    this.isComputing = false;
    this.disableActions = false;
    this.notificationsService.stopSpinner();
  }

  onBack(custom_step: string) {
    if (custom_step == 'volatility') {
      this.selectedPage = this.prevStepPage[custom_step][this.strategyParameters.strategy_type.alias];
    }
    this.onShowHideResults(true);
  }

  async onCancel(custom_step: string) {
    this.notificationsService.runSpinner();
    this.isComputing = true;
    this.disableActions = true;

    try {
      this.strategyParameters.customization(custom_step, false);
      await this.strategiesService.computeCustomStrategy(this.strategyParameters, this.currentCluster);
      this.notificationsService.notify('computation-success');
    } catch (e) {
      // Display error
      this.strategyParameters.customization(custom_step, true);
      this.notificationsService.notify('computation-failed');
    }

    if (this.prevStepPage[custom_step] != undefined) {
      this.selectedPage = this.prevStepPage[custom_step][this.strategyParameters.strategy_type.alias];
    }

    this.onShowHideResults(true);

    this.isComputing = false;
    this.disableActions = false;
    this.notificationsService.stopSpinner();
  }

  onShowHideResults(showResults: boolean) {
    if (!this.showResults && showResults) {
      this.selectedPage2 = 'rolling';
      this.selectedAnalyticsTab2 = 'weights';
    }
    this.showResults = showResults;
    this.updateStepper();
  }

  onAnalyticsTabChange(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Absolute':            this.selectedAnalyticsTab='absolute'; break;
      case 'Relative':            this.selectedAnalyticsTab='relative'; break;
      case 'Rating':              this.selectedAnalyticsTab='rating'; break;
      case 'Robustness':          this.selectedAnalyticsTab='robustness'; break;
    }
  }

  onAnalyticsTabChange2(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Weights':            this.selectedAnalyticsTab2='weights'; break;
      case 'Risk Contributions': this.selectedAnalyticsTab2='risk-contributions'; break;
    }
  }

  onChangeBlock(selectBlock: string) {
    this.changeBlock.emit(selectBlock);
  }

  moveStepper(position: number = 0) {
    this.stepper.reset();
    for (var s = 0; s < position; s++) {
      this.stepper.next();
    }
  }

  updateStepper() {
    let stepPosition: number = 0;
    if (this.defaultStrategy.strategy_type.alias=='EFW' && this.strategyParameters.customize_exposure) {
      stepPosition += 1;
    }
    if (this.defaultStrategy.strategy_type.alias=='EFRP' && this.strategyParameters.customize_risk_contributions) {
      stepPosition += 1;
    }
    if (this.strategyParameters.customize_volatility) {
      stepPosition += 1;
    }
    if (!this.showResults) {
      stepPosition += 1;
    }
    if (this.showResults && this.strategyParameters.customize_volatility) {
      stepPosition += 1;
    }

    this.moveStepper(stepPosition);
  }

  onDateChange(dateStr: string = undefined) {
    this.spotBreakdownData = undefined;
    if (this.customizedStrategy && this.customizedStrategy.factorAnalysis && this.customizedStrategy.factorAnalysis.rebalancing) {
      const rebalandcingData: any = this.customizedStrategy.factorAnalysis.rebalancing;
      if (dateStr == undefined) { dateStr = rebalandcingData.dates[rebalandcingData.dates.length-1]; }

      const iDate = rebalandcingData.dates.indexOf(dateStr);

      let spotBreakdownData = [{
        label: 'weight',
        values: {
          size: rebalandcingData.weights[iDate][0],
          value: rebalandcingData.weights[iDate][1],
          momentum: rebalandcingData.weights[iDate][2],
          volatility: rebalandcingData.weights[iDate][3],
          high_yield: rebalandcingData.weights[iDate][4],
          quality: rebalandcingData.weights[iDate][5],
        }
      }, {
        label: 'risk',
        values: {
          size: rebalandcingData.risk_contributions[iDate][0],
          value: rebalandcingData.risk_contributions[iDate][1],
          momentum: rebalandcingData.risk_contributions[iDate][2],
          volatility: rebalandcingData.risk_contributions[iDate][3],
          high_yield: rebalandcingData.risk_contributions[iDate][4],
          quality: rebalandcingData.risk_contributions[iDate][5],
        },
      }];
      setTimeout(() => {
        this.spotBreakdownData = spotBreakdownData;
      }, 100);
    }
  }

}
